import ConfirmationThanks from "components/Common/ConfirmationThanks/ConfirmationThanks";
import {
	CustomQuoteThanksSubheader,
	NewOrderButton,
	NewOrderConfirmationOrderNumber,
	NewOrderConfirmationOrderPending,
	NewOrderOrderDate,
	NewOrdersHeaderSummaryDetailsAccessoriesCount,
	NewOrdersHeaderSummaryDetailsCabinetsCount,
	NewOrdersHeaderSummaryDetailsCubeCount,
	NewOrdersHeaderSummaryDetailsProductLines,
	NewOrderThanksSubheader,
	NewQuoteOrderThanksSubheader
} from "components/NewOrders/constants";
import {
	AddOnOriginalOrderNumber,
	NewOrderConfirmationOrderListHeader,
	NewOrderNumberHeader,
	NewOrdersContent,
	NewOrdersContentContainer,
	NewOrdersSummaryDetailsDesktop,
	NewOrdersSummaryDetailsMobile
} from "components/NewOrders/NewOrders.styles";
import ReviewOrderDetails from "components/NewOrders/Review/ReviewOrderDetails";
import ReviewOrderList from "components/NewOrders/Review/ReviewOrderList";
import { containsCustomQuoteSku, formatOrderType, todayDate } from "components/NewOrders/utils/NewOrderUtils.tsx";
import { CustomText, OriginalOrderText, UnavailableDataPlaceholderText } from "constants/text";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import useHandlePopState from "hooks/useHandlePopState";
import useInitOrderDetail from "hooks/useInitOrderDetail";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { RootState } from "stores/application.store";
import { dateFormatTwoDigitDays } from "utils/date";
import { epsilonRounding } from "utils/epsilonRounding";
import { PageProps } from "../BuildOrderPage/BuildOrderPage";
import PaintChipInstructions from "../../../components/PaintChipInstructions/PaintChipInstructions.tsx";
import { OrderType } from "types/declarations/enums.ts";
import { useLazyGetDraftOrderQuery } from "features/api/orderApi.ts";
import { useEffect, useState } from "react";
import { models } from "types/api/viewModels.ts";

const NewOrderConfirmation = ({ isAddOn, isNewQuoteOrder }: PageProps) => {
	const dispatch = useDispatch();
	const orderType = useSelector((state: RootState) => state.newOrder.newOrderDetails?.orderType);
	const newOrderDetailsRedux = useSelector((state: RootState) => state.newOrder.newOrderDetails);
	const draftOrder = useSelector((state: RootState) => state.newOrder.draftDetails?.draftOrder);
	const validatedOrderResponse = useSelector((state: RootState) => state.newOrder.validatedOrderResponse);
	const param = useParams();
	const { orderDetail } = useInitOrderDetail(param.orderId);
	const [fetchDraftOrder, { data: loadedDraftOrder }] = useLazyGetDraftOrderQuery();

	const [newOrderDetails, setNewOrderDetails] = useState<
		models["DraftOrderViewModel"] | models["PendingOrderViewModel"] | undefined
	>(newOrderDetailsRedux);

	useEffect(() => {
		fetchDraftOrder(param.draftId ?? "").then((response) => {
			setNewOrderDetails(newOrderDetailsRedux ?? response.data);
		});
	}, [fetchDraftOrder, loadedDraftOrder, newOrderDetailsRedux, param.draftId]);

	useHandlePopState({
		title: isAddOn ? "Add-Ons Confirmation" : "New Order Confirmation",
		// eslint-disable-next-line sonarjs/no-nested-conditional
		path: isAddOn ? `/details/${param.orderId}` : `/new-${isNewQuoteOrder ? "quote" : "order"}`,
		dispatchActions: () => {
			dispatch(newOrderActions.clearNewOrder());
		}
	});
	const showPaintChipInstructions =
		newOrderDetails?.orderType === OrderType.CUSTOM_COLOR &&
		newOrderDetails?.configurations?.find(
			(config) =>
				config.globals?.finish?.customColor?.vendorCode === CustomText ||
				config.globals?.finish?.customColor?.colorCode === CustomText
		);

	const validatedCountsByProductLine = validatedOrderResponse?.countsByProductLine ?? {};
	const draftCountsByProductLine: Record<
		string,
		{ cabinetCount: number; accessoryCount: number; cubeCount: number }
	> = (newOrderDetails?.configurations ?? []).reduce(
		(acc, config) => {
			const productLine = config.globals?.productLine?.description ?? "N/A";
			const lineItems = config.lineItems ?? [];

			acc[productLine] ??= { cabinetCount: 0, accessoryCount: 0, cubeCount: 0 };

			for (const item of lineItems) {
				acc[productLine].cabinetCount += item.cabinetCount ?? 0;
				acc[productLine].accessoryCount += item.accessoryCount ?? 0;
				acc[productLine].cubeCount += item.cubeCount ?? 0;
			}

			return acc;
		},
		{} as Record<string, { cabinetCount: number; accessoryCount: number; cubeCount: number }>
	);
	const countsByProductLine = isNewQuoteOrder ? draftCountsByProductLine : validatedCountsByProductLine;

	const getSubHeader = () => {
		if (containsCustomQuoteSku(validatedOrderResponse)) {
			return CustomQuoteThanksSubheader;
		} else if (isNewQuoteOrder) {
			return NewQuoteOrderThanksSubheader;
		} else {
			return NewOrderThanksSubheader;
		}
	};

	return (
		<div>
			<ConfirmationThanks
				subheader={getSubHeader()}
				button={NewOrderButton}
				isAddOn={isAddOn}
			/>
			<NewOrdersContentContainer>
				{showPaintChipInstructions && <PaintChipInstructions />}
				<NewOrdersContent>
					<NewOrderConfirmationOrderListHeader>
						<div> {formatOrderType(orderType)} </div>
						<div>
							<span data-testid="new-order-confirmation-header">
								{isAddOn && (
									<NewOrderNumberHeader>
										<div>
											<div data-testid="new-order-confirmation-order-number">
												{NewOrderConfirmationOrderNumber}
											</div>
											<div data-testid="new-order-confirmation-pending">
												&nbsp;
												{NewOrderConfirmationOrderPending}
											</div>
										</div>
										<AddOnOriginalOrderNumber>
											{OriginalOrderText} # {orderDetail?.orderNumber}
										</AddOnOriginalOrderNumber>
									</NewOrderNumberHeader>
								)}
							</span>
							<NewOrdersSummaryDetailsMobile
								data-testid="new-orders-header-summary-details"
								isConfirmation={true}
							>
								<div>
									<div>{NewOrderOrderDate}</div>
									<div data-testid="new-order-header-summary-details-order-date">
										{dateFormatTwoDigitDays(String(todayDate))}
									</div>
								</div>
								<div>
									<div>{NewOrdersHeaderSummaryDetailsProductLines}</div>
									{Object.entries(countsByProductLine).map(([productLine]) => (
										<div
											data-testid="new-orders-header-summary-details-product-lines"
											key={productLine}
										>
											{productLine}
										</div>
									))}
								</div>
								{Object.entries(countsByProductLine).map(([productLine, counts]) => (
									<div key={productLine}>
										<div>
											<div>{NewOrdersHeaderSummaryDetailsCabinetsCount}</div>
											<div>{counts?.cabinetCount ?? UnavailableDataPlaceholderText}</div>
										</div>
										<div>
											<div>{NewOrdersHeaderSummaryDetailsAccessoriesCount}</div>
											<div>{counts?.accessoryCount ?? UnavailableDataPlaceholderText}</div>
										</div>
										<div>
											<div>{NewOrdersHeaderSummaryDetailsCubeCount}</div>
											<div>
												{epsilonRounding(
													Number(counts?.cubeCount ?? UnavailableDataPlaceholderText)
												)
													.toFixed(2)
													.toString()}
											</div>
										</div>
									</div>
								))}
							</NewOrdersSummaryDetailsMobile>
							<NewOrdersSummaryDetailsDesktop
								data-testid="new-orders-header-summary-details"
								isConfirmation={true}
							>
								<div>
									<div>{NewOrderOrderDate}</div>
									<div data-testid="new-order-header-summary-details-order-date">
										{dateFormatTwoDigitDays(String(todayDate))}
									</div>
								</div>
								<div>
									<div>{NewOrdersHeaderSummaryDetailsProductLines}</div>
									{Object.entries(countsByProductLine).map(([productLine]) => (
										<div
											data-testid="new-orders-header-summary-details-product-lines"
											key={productLine}
										>
											{productLine}
										</div>
									))}
								</div>
								<div>
									<div>{NewOrdersHeaderSummaryDetailsCabinetsCount}</div>
									{Object.entries(countsByProductLine).map(([productLine, counts]) => (
										<div
											data-testid="new-order-header-summary-details-cabinets-count"
											key={productLine}
										>
											{counts?.cabinetCount ?? UnavailableDataPlaceholderText}
										</div>
									))}
								</div>
								<div>
									<div>{NewOrdersHeaderSummaryDetailsAccessoriesCount}</div>
									{Object.entries(countsByProductLine).map(([productLine, counts]) => (
										<div
											data-testid="new-order-header-summary-details-accessories-count"
											key={productLine}
										>
											{counts?.accessoryCount ?? UnavailableDataPlaceholderText}
										</div>
									))}
								</div>
								<div>
									<div>{NewOrdersHeaderSummaryDetailsCubeCount}</div>
									{Object.entries(countsByProductLine).map(([productLine, counts]) => (
										<div
											data-testid="new-order-header-summary-details-cubes-count"
											key={productLine}
										>
											{epsilonRounding(
												Number(counts?.cubeCount ?? UnavailableDataPlaceholderText)
											)
												.toFixed(2)
												.toString()}
										</div>
									))}
								</div>
							</NewOrdersSummaryDetailsDesktop>
						</div>
					</NewOrderConfirmationOrderListHeader>
					{!isNewQuoteOrder && <ReviewOrderDetails />}
					<ReviewOrderList
						isConfirmationPage
						isAddOn={isAddOn}
						isNewQuoteOrder={isNewQuoteOrder}
						internalQuoteDetails={param.draftId ? newOrderDetails : (draftOrder ?? newOrderDetails)}
					/>
				</NewOrdersContent>
			</NewOrdersContentContainer>
		</div>
	);
};

export default NewOrderConfirmation;
